// aiOpsLog — fire-and-forget event-log för intent, moderation, artifact,
// regression, model-usage. Skriver till ai_ops_event via RPC.
//
// Fel tolereras tyst — ai-ops-loggning är observability, inte funktionellt
// kritiskt. Ingen retry, ingen toast.

import { callRpc } from '@/lib/supabaseTyped';
import { logger } from '@/lib/logger';
import type { Intent, IntentSource } from './intentTypes';

type Payload = Record<string, unknown> | null;

interface LogArgs {
  eventType: string;
  payload?: Payload;
  conversationId?: string;
  artifactId?: string;
  versionId?: string;
  moderationEventId?: string;
}

async function emit(args: LogArgs): Promise<void> {
  // Alltid logga till debug-buffer — tillgänglig via __FLOW_DEBUG även
  // om DB-write failar (t.ex. offline eller migration inte körd).
  logger.debug(`[aiOps] ${args.eventType}`, {
    payload: args.payload,
    conversationId: args.conversationId,
    artifactId: args.artifactId,
    moderationEventId: args.moderationEventId,
  });

  try {
    await callRpc('log_ai_ops_event', {
      p_event_type: args.eventType,
      p_payload: args.payload ?? null,
      p_conversation_id: args.conversationId ?? null,
      p_artifact_id: args.artifactId ?? null,
      p_version_id: args.versionId ?? null,
      p_moderation_event_id: args.moderationEventId ?? null,
    });
  } catch (err) {
    // Observability-emit ska aldrig kasta — sväl felet.
    logger.warn('[aiOps] DB emit failed', {
      eventType: args.eventType,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Namespace-funktioner för alla ai_ops-events. Signaturer är typade
 * för att minimera fältfel i call-sites.
 */
export const aiOps = {
  intentClassified: (args: {
    conversationId?: string;
    intent: Intent;
    confidence: number;
    source: IntentSource;
    reasoning?: string;
  }) =>
    emit({
      eventType: 'intent_classified',
      conversationId: args.conversationId,
      payload: {
        intent: args.intent,
        confidence: args.confidence,
        source: args.source,
        reasoning: args.reasoning,
      },
    }),

  moderationFlag: (args: {
    conversationId?: string;
    moderationEventId: string;
    ruleId: string;
    severity: 'advisory' | 'violation';
    appealed?: boolean;
  }) =>
    emit({
      eventType: 'moderation_flag',
      conversationId: args.conversationId,
      moderationEventId: args.moderationEventId,
      payload: {
        rule_id: args.ruleId,
        severity: args.severity,
        appealed: args.appealed ?? false,
      },
    }),

  artifactCreated: (args: {
    conversationId?: string;
    artifactId: string;
    versionId: string;
    versionNumber: number;
    operation: Intent;
  }) =>
    emit({
      eventType: 'artifact_created',
      conversationId: args.conversationId,
      artifactId: args.artifactId,
      versionId: args.versionId,
      payload: {
        version_number: args.versionNumber,
        operation: args.operation,
      },
    }),

  regressionDetected: (args: {
    conversationId?: string;
    artifactId: string;
    versionId: string;
    missingBlocks: number;
    renamedBlocks: number;
    removedConnectors: number;
  }) =>
    emit({
      eventType: 'regression_detected',
      conversationId: args.conversationId,
      artifactId: args.artifactId,
      versionId: args.versionId,
      payload: {
        missing_blocks: args.missingBlocks,
        renamed_blocks: args.renamedBlocks,
        removed_connectors: args.removedConnectors,
      },
    }),

  modelUsed: (args: { model: string; conversationId?: string; tokenCount?: number }) =>
    emit({
      eventType: 'model_used',
      conversationId: args.conversationId,
      payload: {
        model: args.model,
        token_count: args.tokenCount,
      },
    }),

  modelGateBlocked: (args: {
    requestedModel: string;
    userTier: string;
    conversationId?: string;
  }) =>
    emit({
      eventType: 'model_gate_blocked',
      conversationId: args.conversationId,
      payload: {
        requested_model: args.requestedModel,
        user_tier: args.userTier,
      },
    }),
};

/** Aggregat-resultat från ai_ops_aggregate-RPC. */
export interface AiOpsAggregate {
  success: boolean;
  window_hours: number;
  intent_distribution: Array<{ intent: string; source: string; count: number }>;
  moderation_funnel: Array<{ severity: string; appealed: boolean; count: number }>;
  regression: { total_extends: number; regressions_detected: number };
  model_usage: Array<{ model: string; count: number }>;
}

export async function fetchAiOpsAggregate(
  withinHours = 24,
): Promise<AiOpsAggregate | null> {
  const { data } = await callRpc<AiOpsAggregate>('ai_ops_aggregate', {
    p_within_hours: withinHours,
  });
  return data ?? null;
}
